import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Building2, Loader2, RefreshCw, Plus, CheckCircle2, AlertCircle, ExternalLink, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall } from '@/lib/api';

interface Org {
  id: string;
  name: string;
  slug: string;
  custom_domain: string | null;
  stripe_account_id: string | null;
  stripe_account_status: string | null;
  stripe_connected_at: string | null;
  application_fee_bps: number;
  public_signup_enabled: number;
  member_count: number;
  active_member_count: number;
}

export default function Orgs() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<Org[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const token = await getToken();
      const r = await apiCall<{ items: Org[] }>('/v1/orgs', { bearerToken: token });
      setItems(r.items);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => {
    void load();
    // Show toast based on connect=ok / declined / error in URL
    const params = new URLSearchParams(window.location.search);
    const connect = params.get('connect');
    if (connect) {
      const msg = connect === 'ok' ? '✓ Stripe connected.'
        : connect === 'declined' ? 'Stripe connection declined.'
        : 'Stripe connection failed.';
      alert(msg);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [load]);

  async function connectStripe(orgId: string) {
    setConnecting(orgId);
    try {
      const token = await getToken();
      const r = await apiCall<{ url: string }>(`/v1/orgs/${orgId}/connect/start`, { bearerToken: token });
      window.location.href = r.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Connect failed');
      setConnecting(null);
    }
  }

  async function disconnectStripe(orgId: string) {
    if (!confirm('Disconnect this gym\'s Stripe? New signups + donations will be blocked until they reconnect.')) return;
    try {
      const token = await getToken();
      await apiCall(`/v1/orgs/${orgId}/connect/disconnect`, { method: 'POST', bearerToken: token });
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Disconnect failed'); }
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Building2 className="h-6 w-6" /> Organizations</h1>
          <p className="text-sm text-muted-foreground">
            Each customer (UWC, future gyms) is one row here. Member payments flow to their Stripe account via Stripe Connect.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}><Plus className="mr-1 h-4 w-4" /> New org</Button>
        </div>
      </div>

      {err && <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {err}</CardContent></Card>}

      {!loading && items.length === 0 && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          No organizations yet. Click <strong>New org</strong> to add one.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {items.map((o) => (
          <Card key={o.id}>
            <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-lg">{o.name}</span>
                  <Badge variant="outline" className="font-mono text-xs">{o.slug}</Badge>
                  {o.custom_domain && <Badge variant="outline" className="text-xs">{o.custom_domain}</Badge>}
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                  <span>{o.active_member_count} active · {o.member_count} total members</span>
                  {o.application_fee_bps > 0 && <span>Platform fee: {(o.application_fee_bps / 100).toFixed(2)}%</span>}
                  {!o.public_signup_enabled && <Badge variant="outline" className="text-xs">Signup disabled</Badge>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {o.stripe_account_id ? (
                  <>
                    <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Stripe connected</Badge>
                    <Button size="sm" variant="ghost" className="h-7 text-muted-foreground hover:text-destructive" onClick={() => void disconnectStripe(o.id)}>Disconnect</Button>
                  </>
                ) : (
                  <Button size="sm" onClick={() => void connectStripe(o.id)} disabled={connecting === o.id}>
                    {connecting === o.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1" />}
                    Connect Stripe
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showCreate && <CreateOrgDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); void load(); }} />}
    </div>
  );
}

function CreateOrgDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [feeBps, setFeeBps] = useState('0');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const token = await getToken();
      await apiCall('/v1/orgs', {
        method: 'POST', bearerToken: token,
        body: {
          name: name.trim(),
          slug: slug.trim(),
          custom_domain: customDomain.trim() || null,
          application_fee_bps: parseInt(feeBps, 10) || 0,
        },
      });
      onCreated();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed'); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Add organization</h2>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
            </div>
            <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Display name *</span>
              <input value={name} onChange={(e) => setName(e.target.value)} required className="w-full border rounded px-2 py-1.5 text-sm" /></label>
            <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Slug *</span>
              <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} required placeholder="uwc" className="w-full border rounded px-2 py-1.5 text-sm font-mono" /></label>
            <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Custom domain (optional)</span>
              <input value={customDomain} onChange={(e) => setCustomDomain(e.target.value.toLowerCase())} placeholder="unitedwrestlingclub.com" className="w-full border rounded px-2 py-1.5 text-sm" /></label>
            <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">Platform fee (basis points, 0–1000)</span>
              <input type="number" min="0" max="1000" value={feeBps} onChange={(e) => setFeeBps(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" />
              <span className="block text-xs text-muted-foreground mt-1">100 bps = 1% taken on member transactions. Leave 0 to bill SaaS separately.</span></label>
            {err && <p className="text-sm text-destructive">{err}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={!name.trim() || !slug.trim() || busy}>
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                Create
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}
